import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { OrganizationService } from '@/services/organizations';
import { supabaseManager } from '@/services/supabaseManager';
import { queryKeys } from '@/lib/queryKeys';
import { STALE_TIME_AUTH } from '@/lib/queryCacheConfig';
import type { Organization } from '@/types';
import type { OrganizationMembership } from '@/types';
import { useAuthStore } from '@/stores/authStore';
import { isSimpleOperator } from '@/stores/auth/auth.operator';
import { navigateTo } from '@/utils/navigationUtils';

export interface OrganizationData {
  organization: Organization | null;
  membership: OrganizationMembership | null;
}

export const useOrganizationData = (slug: string | null) => {
  const { user, loading: authLoading, isAuthenticated, handleApiError } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.organization(slug ?? ''),
    queryFn: async (): Promise<OrganizationData> => {
      if (!slug || !user) {
        return { organization: null, membership: null };
      }

      try {
        try {
          await supabaseManager.connectToOrganizationBySlug(slug);
        } catch (connectionError) {
          navigateTo('/fast-switch');
          throw connectionError;
        }

        const organization = await OrganizationService.getOrganizationBySlug(slug);

        const authSnap = useAuthStore.getState();
        if (
          isSimpleOperator(authSnap.activeOperator) &&
          authSnap.simpleUser &&
          authSnap.simpleUser.organization_id === organization.id
        ) {
          const synthetic: OrganizationMembership = {
            id: `simple:${authSnap.simpleUser.id}`,
            userId: authSnap.user?.id ?? '',
            organizationId: organization.id,
            role: authSnap.simpleUser.role === 'manager' ? 'manager' : 'cashier',
            isActive: true,
            joinedAt: new Date(),
          };
          return { organization, membership: synthetic };
        }

        try {
          const membership = await OrganizationService.getMembershipByUserAndOrg(user.id, organization.id);
          return { organization, membership };
        } catch (membershipError) {
          const refreshed = await handleApiError(membershipError);
          if (refreshed) {
            throw membershipError;
          }
          console.warn('[useOrganizationData] Failed to load membership:', membershipError);
          return { organization, membership: null };
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : String(error);
        if (msg.includes('No organization client connected')) {
          navigateTo('/fast-switch');
          throw error;
        }
        const refreshed = await handleApiError(error);
        if (refreshed) {
          throw error;
        }
        console.error('[useOrganizationData] Failed to load organization:', error);
        throw error;
      }
    },
    enabled: !!slug && !!user && isAuthenticated && !authLoading,
    staleTime: STALE_TIME_AUTH.organization,
    retry: 1,
    retryDelay: 1000,
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: async (updatedData: Partial<Organization>) => {
      const data = queryClient.getQueryData<OrganizationData>(queryKeys.organization(slug ?? ''));
      if (!data?.organization?.id) {
        throw new Error('No organization loaded');
      }
      return OrganizationService.updateOrganization(data.organization.id, updatedData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.organization(slug ?? '') });
    },
  });

  const invalidateOrganization = () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.organization(slug ?? '') });
  };

  const updateOrganization = async (updatedData: Partial<Organization>): Promise<boolean> => {
    try {
      await updateOrganizationMutation.mutateAsync(updatedData);
      return true;
    } catch {
      return false;
    }
  };

  return {
    ...query,
    organization: query.data?.organization ?? null,
    membership: query.data?.membership ?? null,
    invalidateOrganization,
    updateOrganization,
    isUpdatingOrganization: updateOrganizationMutation.isPending,
  };
};
